import { useState, useEffect, useContext, useCallback } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import {
  Loader2,
  MapPin,
  Calendar,
  Users,
  AlertCircle,
  XCircle,
  Lock,
  Globe2,
  Gauge,
  Smartphone,
  Share2,
  Copy,
  Check,
  Image
} from 'lucide-react';
import axios from '../lib/apiTransport';
import { toast } from 'sonner';
import { getUserTimezone } from '../utils/datetime';
import { deriveRunLifecycle, RUN_LIFECYCLE } from '../utils/runLifecycle';
import { getRunNoun } from '../utils/runNoun';
import { APP_STORE_URL, isIPhoneWebSafari, isDesktopWeb } from '../lib/appLinks';
import InstagramShareSheet from '../components/InstagramShareSheet';
import {
  getDobRetryMessage,
  isDobRequiredError,
  PARTICIPATION_INTENT_TYPES,
  saveParticipationDobIntent,
} from '../lib/participationDob';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL.replace(/\/+$/, '')}/api`;

const RunPreview = () => {
  const { runId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useContext(AuthContext);

  const searchParams = new URLSearchParams(location.search);
  const clubId = searchParams.get('club');

  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [joining, setJoining] = useState(false);
  const [waitlisting, setWaitlisting] = useState(false);
  const [waitlisted, setWaitlisted] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [instagramShareOpen, setInstagramShareOpen] = useState(false);

  const returnTo = clubId ? `/run/${runId}?club=${clubId}` : `/run/${runId}`;

  useEffect(() => {
    if (!location.state?.dobCompletionSuccess) return;
    toast.success(getDobRetryMessage(location.state.participationIntentType));
    navigate(returnTo, { replace: true, state: null });
  }, [location.state, navigate, returnTo]);

  const requestParticipationDob = () => {
    const saved = saveParticipationDobIntent({
      type: PARTICIPATION_INTENT_TYPES.DIRECT_JOIN,
      returnTo,
    });
    if (!saved) {
      toast.error('Unable to open date of birth entry. Please try again.');
      return false;
    }
    navigate('/complete-dob');
    return true;
  };

  const trackShareEvent = useCallback(async (eventType) => {
    try {
      await axios.post(`${API}/share/events`, {
        event_type: eventType,
        run_id: runId,
        source: 'run_preview',
      });
    } catch {
      // Analytics should never block preview or join UX.
    }
  }, [runId]);

  const beginAuthentication = (intent) => {
    localStorage.setItem('pendingRunId', runId);
    if (clubId) {
      localStorage.setItem('pendingClubId', clubId);
    }
    try {
      sessionStorage.setItem('openruns.publicGameAuthRunId', runId);
    } catch {
      // Analytics provenance is optional; authentication return state is above.
    }
    trackShareEvent('authentication_started');
    navigate('/', {
      state: {
        showAuth: true,
        returnTo,
        pendingRunId: runId,
        pendingClubId: clubId,
        participationIntent: intent,
      }
    });
  };

  const fetchRunPreview = useCallback(async () => {
    if (!runId) {
      setError('Invalid link');
      setErrorCode('invalid');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/runs/${runId}/preview`);
      setRun(response.data);
    } catch (err) {
      console.error('Failed to fetch run:', err);
      if (err.response?.status === 404) {
        setError('This run was not found');
        setErrorCode('not_found');
      } else {
        setError('Unable to load run details');
        setErrorCode('error');
      }
    } finally {
      setLoading(false);
    }
  }, [runId]);

  const checkEligibility = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/runs/${runId}/join-eligibility`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      setEligibility(response.data);
    } catch (err) {
      console.error('Failed to check eligibility:', err);
      setEligibility({ can_join: true });
    }
  }, [runId]);

  useEffect(() => {
    if (runId) {
      trackShareEvent('link_open');
    }
  }, [runId, trackShareEvent]);

  useEffect(() => {
    fetchRunPreview();
  }, [fetchRunPreview]);

  useEffect(() => {
    if (token && runId && run) {
      checkEligibility();
    }
  }, [token, runId, run, checkEligibility]);

  useEffect(() => {
    if (!token || !runId) return;
    try {
      if (sessionStorage.getItem('openruns.publicGameAuthRunId') !== runId) return;
      sessionStorage.removeItem('openruns.publicGameAuthRunId');
      trackShareEvent('authentication_completed');
    } catch {
      // Analytics should never block preview or participation UX.
    }
  }, [runId, token, trackShareEvent]);

  const handleJoinClick = () => {
    setActionError(null);
    trackShareEvent('join_action_selected');
    if (!token) {
      beginAuthentication('join');
      return;
    }

    if (!user?.date_of_birth) {
      requestParticipationDob();
      return;
    }

    handleJoin();
  };

  const handleJoin = async () => {
    setActionError(null);
    setJoining(true);
    try {
      const response = await axios.post(`${API}/games/${runId}/join/team_a`, null, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });

      trackShareEvent('join_conversion');
      navigate(`/games/${runId}`, response.data?.idempotent ? undefined : {
        state: { iosInstallAfterJoin: { id: runId, userId: user.id } },
      });
    } catch (err) {
      if (isDobRequiredError(err)) {
        requestParticipationDob();
        return;
      }
      console.error('Failed to join:', err);
      const detail = err.response?.data?.detail || 'Unable to join this run';
      setActionError(typeof detail === 'string' ? detail : 'Unable to join this run');
      checkEligibility();
    } finally {
      setJoining(false);
    }
  };

  const handleWaitlistClick = async () => {
    setActionError(null);
    trackShareEvent('waitlist_action_selected');
    if (!token) {
      beginAuthentication('waitlist');
      return;
    }
    if (!user?.date_of_birth) {
      const saved = saveParticipationDobIntent({
        type: PARTICIPATION_INTENT_TYPES.WAITLIST_JOIN,
        returnTo,
      });
      if (!saved) {
        toast.error('Unable to open date of birth entry. Please try again.');
        return;
      }
      navigate('/complete-dob');
      return;
    }

    setWaitlisting(true);
    try {
      await axios.post(`${API}/games/${runId}/waitlist/join`, null, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      setWaitlisted(true);
      trackShareEvent('waitlist_conversion');
      toast.success('You joined the waitlist.');
    } catch (err) {
      if (isDobRequiredError(err)) {
        const saved = saveParticipationDobIntent({
          type: PARTICIPATION_INTENT_TYPES.WAITLIST_JOIN,
          returnTo,
        });
        if (saved) navigate('/complete-dob');
        else toast.error('Unable to open date of birth entry. Please try again.');
        return;
      }
      const detail = err.response?.data?.detail;
      setActionError(typeof detail === 'string' ? detail : 'Unable to join the waitlist');
    } finally {
      setWaitlisting(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const userTimezone = getUserTimezone();
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: userTimezone,
      });
    } catch {
      return '';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const userTimezone = getUserTimezone();
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        timeZone: userTimezone,
      });
    } catch {
      return '';
    }
  };

  const getShareUrl = () => run?.share_url || `https://openrunsusa.com/run/${runId}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      setCopied(true);
      trackShareEvent('copy_click');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy link');
    }
  };

  const handleNativeShare = async () => {
    const shareUrl = getShareUrl();
    const publicType = run?.public_type === 'training' ? 'training' : 'run';
    const title = publicType === 'training' ? 'Basketball training on OpenRuns' : 'Open run on OpenRuns';
    const publicShareMessage = String(run?.public_share_message || '').trim();
    const text = publicShareMessage || (publicType === 'training'
      ? `Basketball training in ${run?.public_location_label || run?.city || 'your area'}`
      : `Open run in ${run?.public_location_label || run?.city || 'your area'}`);

    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title, text, url: shareUrl });
        trackShareEvent('share_click');
        return;
      } catch (err) {
        if (err?.name === 'AbortError') return;
      }
    }

    await handleCopyLink();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (error && errorCode === 'not_found') {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center px-6">
        <div className="text-center max-w-sm rounded-3xl bg-white p-8 shadow-xl">
          <XCircle className="w-16 h-16 text-zinc-300 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-zinc-900 mb-2">Run Not Found</h1>
          <p className="text-zinc-500 mb-8">This run may have been cancelled or removed.</p>
          <Button
            onClick={() => navigate('/dashboard')}
            className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-xl h-12 px-8"
          >
            Browse Runs
          </Button>
        </div>
      </div>
    );
  }

  if (error && !run) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center px-6">
        <div className="text-center max-w-sm rounded-3xl bg-white p-8 shadow-xl">
          <AlertCircle className="w-16 h-16 text-zinc-300 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-zinc-900 mb-2">Unable to Load</h1>
          <p className="text-zinc-500 mb-8">{error}</p>
          <Button
            onClick={() => window.location.reload()}
            variant="outline"
            className="rounded-xl h-12 px-8"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  const runNoun = getRunNoun(run);
  if (run?.is_cancelled) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center px-6">
        <div className="text-center max-w-sm rounded-3xl bg-white p-8 shadow-xl">
          <XCircle className="w-16 h-16 text-zinc-300 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-zinc-900 mb-2">{runNoun} Cancelled</h1>
          <p className="text-zinc-500 mb-8">This {runNoun.toLowerCase()} has been cancelled.</p>
          <Button
            onClick={() => navigate('/dashboard')}
            className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-xl h-12 px-8"
          >
            Browse Runs
          </Button>
        </div>
      </div>
    );
  }

  const isFull = run?.is_full;
  const cantJoin = eligibility && !eligibility.can_join;
  const cantJoinReason = eligibility?.reason;
  const cantJoinCode = eligibility?.reason_code;
  const previewLifecycle = deriveRunLifecycle({ status: run?.status, date_time: run?.date_time });

  if (previewLifecycle === RUN_LIFECYCLE.COMPLETED || previewLifecycle === RUN_LIFECYCLE.AWAITING_OUTCOME) {
    const isCompleted = previewLifecycle === RUN_LIFECYCLE.COMPLETED;
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center px-6" data-testid="run-preview-ended">
        <div className="text-center max-w-sm rounded-3xl bg-white p-8 shadow-xl">
          <Calendar className="w-16 h-16 text-zinc-300 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-zinc-900 mb-2">
            {isCompleted ? `${runNoun} Completed` : `${runNoun} Ended`}
          </h1>
          <p className="text-zinc-500 mb-8">
            {isCompleted ? 'This run has wrapped up.' : 'This run has already taken place.'}
          </p>
          <Button
            onClick={() => navigate('/dashboard')}
            className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-xl h-12 px-8"
            data-testid="browse-runs-btn"
          >
            Browse Runs
          </Button>
        </div>
      </div>
    );
  }

  const publicType = run?.public_type === 'training' ? 'training' : 'run';
  const previewNoun = publicType === 'training' ? 'Training' : 'Run';
  const previewLocation = run?.public_location_label || run?.city || 'Location shared after joining';
  const typeLabel = publicType === 'training' ? 'Training' : 'Open Run';
  const rawPreviewTitle = String(run?.public_title || run?.title || '').trim();
  const genericTitles = new Set([
    '',
    'pickup run',
    'open run',
    'run',
    'training',
    'basketball training'
  ]);
  const hasCustomTitle = rawPreviewTitle && !genericTitles.has(rawPreviewTitle.toLowerCase());
  const headline = hasCustomTitle
    ? rawPreviewTitle
    : (previewLocation === 'Location shared after joining' ? typeLabel : `${typeLabel} at ${previewLocation}`);
  const joinedCount = Math.max(0, (run?.max_players || 0) - (run?.spots_left || 0));
  const spotText = run?.spots_left === 1 ? 'spot open' : 'spots open';
  const capacityLabel = `${joinedCount} of ${run?.max_players || 0} joined`;
  const waitlistAvailable = isFull && run?.status === 'waitlist_available';
  const spotsLabel = run?.spots_left > 0
    ? `${run.spots_left} ${spotText}`
    : (waitlistAvailable ? 'Waitlist available' : 'No open spots');
  const visibilityLabel = run?.is_private ? 'Private' : 'Public';
  const skillLabel = [run?.skill_level_min, run?.skill_level_max].filter(Boolean).join(' – ');
  const formatLabel = run?.court_type && run.court_type !== 'training'
    ? String(run.court_type).replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
    : null;
  const publicShareMessage = String(run?.public_share_message || '').trim();
  const shareAvailable = typeof navigator !== 'undefined' && navigator.share;

  const handleOpenInstagramShare = () => {
    setInstagramShareOpen(true);
    trackShareEvent('instagram_share_card_opened');
  };

  return (
    <div className="min-h-screen bg-zinc-100 flex flex-col" data-testid="run-preview-page">
      <div className="flex-1 px-4 py-5 sm:px-6 sm:py-8">
        <div className="mx-auto flex min-h-[calc(100vh-2.5rem)] w-full max-w-lg flex-col justify-center">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-orange-500 text-sm font-black text-white">
                OR
              </div>
              <div>
                <p className="text-sm font-bold text-zinc-950">OpenRuns</p>
                <p className="text-xs text-zinc-500">Pickup basketball, organized</p>
              </div>
            </div>
            <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-zinc-600 shadow-sm">
              {typeLabel}
            </span>
          </div>

          <div className="overflow-hidden rounded-[28px] border border-zinc-200 bg-white shadow-xl shadow-zinc-200/70">
            <div className="bg-zinc-950 px-6 py-7 text-white">
              <p className="mb-3 inline-flex rounded-full bg-orange-500/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-orange-200">
                Shared on OpenRuns
              </p>
              <h1
                className="text-3xl font-black leading-tight tracking-tight sm:text-4xl"
                data-testid="run-title"
              >
                <span data-testid="run-location">{headline}</span>
              </h1>
              <p className="mt-4 max-w-sm text-sm leading-6 text-zinc-300">
                {publicShareMessage || 'Join through OpenRuns to reserve your spot and get updates.'}
              </p>
            </div>

            <div className="space-y-5 px-6 py-6">
              <div className="grid grid-cols-1 gap-3">
                <div className="flex items-center gap-3 rounded-2xl bg-zinc-50 p-4">
                  <Calendar className="h-5 w-5 shrink-0 text-orange-500" />
                  <div>
                    <p className="text-sm font-semibold text-zinc-950" data-testid="run-date">
                      {formatDate(run?.date_time)}
                    </p>
                    <p className="text-sm text-zinc-500" data-testid="run-time">
                      {formatTime(run?.date_time)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 rounded-2xl bg-zinc-50 p-4">
                  <MapPin className="h-5 w-5 shrink-0 text-orange-500" />
                  <div>
                    <p className="text-sm font-semibold text-zinc-950" data-testid="run-city">
                      {previewLocation}
                    </p>
                    <p className="text-sm text-zinc-500">
                      {run?.location_precision === 'area' ? 'Exact details after joining' : 'Public venue'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 rounded-2xl bg-zinc-50 p-4">
                  <Users className="h-5 w-5 shrink-0 text-orange-500" />
                  <div>
                    <p className="text-sm font-semibold text-zinc-950" data-testid="run-players">
                      {capacityLabel}
                    </p>
                    <p className="text-sm text-zinc-500">{spotsLabel}</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700">
                  Basketball {previewNoun}
                </span>
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-3 py-1 text-xs font-semibold text-zinc-700"
                  data-testid="run-visibility"
                >
                  {run?.is_private ? <Lock className="h-3 w-3" /> : <Globe2 className="h-3 w-3" />}
                  {visibilityLabel}
                </span>
                {formatLabel && (
                  <span className="rounded-full bg-zinc-100 px-3 py-1 text-xs font-semibold text-zinc-700">
                    {formatLabel}
                  </span>
                )}
                {skillLabel && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-3 py-1 text-xs font-semibold text-zinc-700">
                    <Gauge className="h-3 w-3" />
                    {skillLabel}
                  </span>
                )}
                {run?.host_display_name && (run.host_user_id ? (
                  <Link to={`/host/${run.host_user_id}`} className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 hover:underline">
                    Hosted by {run.host_display_name}
                  </Link>
                ) : <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">Hosted by {run.host_display_name}</span>)}
              </div>

              {cantJoin && cantJoinCode !== 'already_joined' && (
                <div
                  className="rounded-2xl border border-zinc-200 bg-zinc-50 p-4"
                  data-testid="eligibility-error"
                >
                  {cantJoinCode === 'age_restricted' && (
                    <div className="flex items-start gap-3">
                      <Lock className="w-5 h-5 text-zinc-500 mt-0.5" />
                      <p className="text-sm text-zinc-700">{cantJoinReason}</p>
                    </div>
                  )}
                  {cantJoinCode === 'blocked' && (
                    <p className="text-sm text-zinc-700">{cantJoinReason}</p>
                  )}
                  {cantJoinCode === 'full' && (
                    <p className="text-sm font-medium text-zinc-700">{runNoun} Full</p>
                  )}
                  {!['age_restricted', 'blocked', 'full'].includes(cantJoinCode) && (
                    <p className="text-sm text-zinc-700">{cantJoinReason}</p>
                  )}
                </div>
              )}

              {cantJoinCode === 'already_joined' && (
                <div className="rounded-2xl border border-green-100 bg-green-50 p-4">
                  <p className="text-sm font-medium text-green-700">You're already in this run!</p>
                </div>
              )}

              <div className="space-y-3">
                {waitlisted ? (
                  <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4 text-center" data-testid="waitlist-success">
                    <p className="font-semibold text-blue-800">You're on the waitlist.</p>
                    <p className="mt-1 text-sm text-blue-700">OpenRuns will notify you if a spot becomes available.</p>
                  </div>
                ) : cantJoinCode === 'already_joined' ? (
                  <Button
                    onClick={() => navigate(`/games/${runId}`)}
                    className="w-full h-14 rounded-2xl text-base font-bold bg-[#F97316] hover:bg-[#EA580C] text-white transition-all duration-150 active:scale-[0.98]"
                    data-testid="view-run-btn"
                  >
                    View {runNoun}
                  </Button>
                ) : waitlistAvailable ? (
                  <Button
                    onClick={handleWaitlistClick}
                    disabled={waitlisting}
                    className="w-full h-14 rounded-2xl text-base font-bold bg-[#F97316] hover:bg-[#EA580C] text-white transition-all duration-150 active:scale-[0.98]"
                    data-testid="join-waitlist-btn"
                  >
                    {waitlisting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : !token ? (
                      'Sign In or Create Account to Join Waitlist'
                    ) : (
                      'Join Waitlist'
                    )}
                  </Button>
                ) : isFull ? (
                  <Button
                    disabled
                    className="w-full h-14 rounded-2xl text-base font-bold bg-zinc-200 text-zinc-500 cursor-not-allowed"
                    data-testid="join-btn-disabled"
                  >
                    {runNoun} Full · Waitlist Unavailable
                  </Button>
                ) : cantJoinCode === 'dob_required' ? (
                  <Button
                    onClick={requestParticipationDob}
                    className="w-full h-14 rounded-2xl text-base font-bold bg-[#F97316] hover:bg-[#EA580C] text-white transition-all duration-150 active:scale-[0.98]"
                    data-testid="complete-dob-to-join-btn"
                  >
                    Add Date of Birth to Join
                  </Button>
                ) : cantJoin && cantJoinCode !== 'already_joined' ? (
                  <Button
                    disabled
                    className="w-full h-14 rounded-2xl text-base font-bold bg-zinc-200 text-zinc-500 cursor-not-allowed"
                    data-testid="join-btn-disabled"
                  >
                    {cantJoinCode === 'full' ? `${runNoun} Full` : 'Cannot Join'}
                  </Button>
                ) : (
                  <Button
                    onClick={handleJoinClick}
                    disabled={joining}
                    className="w-full h-14 rounded-2xl text-base font-bold bg-[#F97316] hover:bg-[#EA580C] text-white transition-all duration-150 active:scale-[0.98]"
                    data-testid="join-run-btn"
                  >
                    {joining ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : !token ? (
                      'Sign In or Create Account to Join'
                    ) : (
                      `Join ${runNoun}`
                    )}
                  </Button>
                )}

                {!token && (!isFull || waitlistAvailable) && (
                  <p className="text-center text-sm leading-5 text-zinc-500" data-testid="auth-explanation">
                    {waitlistAvailable
                      ? 'Sign in or create an account, then you’ll return here to join the waitlist.'
                      : 'Sign in or create an account, then you’ll return here to join. No spot is reserved until you finish.'}
                  </p>
                )}
                {actionError && (
                  <p className="rounded-xl bg-red-50 px-3 py-2 text-center text-sm text-red-700" role="alert">
                    {actionError}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3 border-t border-zinc-100 pt-4">
                <Button
                  type="button"
                  onClick={handleOpenInstagramShare}
                  className="col-span-2 h-12 rounded-2xl gap-2 bg-zinc-950 font-semibold text-white hover:bg-zinc-800"
                  data-testid="instagram-share-btn"
                >
                  <Image className="h-4 w-4" />
                  Share to Instagram
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCopyLink}
                  className="h-12 rounded-2xl gap-2 font-semibold"
                  data-testid="copy-link-btn"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                  {copied ? 'Copied' : 'Copy Link'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleNativeShare}
                  className="h-12 rounded-2xl gap-2 font-semibold"
                  data-testid="native-share-btn"
                >
                  <Share2 className="h-4 w-4" />
                  {shareAvailable ? 'Share' : 'Copy'}
                </Button>
              </div>
            </div>
          </div>

          {isIPhoneWebSafari() && (
            <div className="mt-6 flex justify-center">
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white hover:bg-zinc-50 text-zinc-900 text-sm font-medium transition-colors shadow-sm"
                data-testid="ios-app-cta"
              >
                <Smartphone className="w-4 h-4" />
                Get the iOS app
              </a>
            </div>
          )}
          {isDesktopWeb() && (
            <p
              className="mt-6 text-center text-xs text-zinc-500"
              data-testid="desktop-phone-hint"
            >
              For the best experience, open this link on your phone.
            </p>
          )}
        </div>
      </div>
      <InstagramShareSheet
        open={instagramShareOpen}
        onOpenChange={setInstagramShareOpen}
        headline={headline}
        typeLabel={typeLabel}
        dateLabel={formatDate(run?.date_time)}
        timeLabel={formatTime(run?.date_time)}
        locationLabel={previewLocation}
        capacityLabel={capacityLabel}
        spotsLabel={spotsLabel}
        shareUrl={getShareUrl()}
        publicShareMessage={publicShareMessage}
        publicType={publicType}
        onTrack={trackShareEvent}
      />
    </div>
  );
};

export default RunPreview;
